import { useState, useEffect, useCallback } from 'react'
import { useRecoilState } from 'recoil'

import { getOfficeNotificationsByDate } from '../db/db'
import CalendarService from '../services/CalendarService'
import { sessionState } from '../state'
import Database from '../types/Database'
import { CalendarData, CalendarActions } from '../types/ServiceTypes'

/**
 * Custom hook for managing calendar state and operations
 * Extracted from components to improve separation of concerns
 */
export function useCalendarData(
  initialStartDate: Date = new Date(),
  initialNumOfDays: number = 7
): [CalendarData, CalendarActions] {
  const [session, setSession] = useRecoilState(sessionState)
  const [notifications, setNotifications] = useState<
    Record<string, Database.OfficeNotification[]>
  >({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Calendar data derived from session state and local state
  const calendarData: CalendarData = {
    startDate: initialStartDate,
    numOfDays: initialNumOfDays,
    bookings: session.bookings || [],
    notifications,
    selectedFromDate: session.selectedFromDate,
    selectedRecurAmount: session.selectedRecurAmount,
    selectedRecurUnit: session.selectedRecurUnit,
    loading,
    error
  }

  /**
   * Refresh bookings for a new date selection
   */
  const refreshBookingsForDate = useCallback(
    async (newDate: Date) => {
      try {
        setLoading(true)
        setError(null)

        const result = await CalendarService.refreshBookingsForDate(
          newDate,
          session.selectedRecurAmount,
          session.selectedRecurUnit,
          session.offices || []
        )

        setSession(old => ({
          ...old,
          selectedFromDate: result.selectedFromDate,
          bookings: result.bookings
        }))
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to refresh bookings'
        setError(errorMessage)
        console.error('Error refreshing bookings for date:', err)
      } finally {
        setLoading(false)
      }
    },
    [
      session.selectedRecurAmount,
      session.selectedRecurUnit,
      session.offices,
      setSession
    ]
  )

  /**
   * Set the selected recurrence amount
   */
  const setSelectedRecurAmount = useCallback(
    (amount: number) => {
      setSession(old => ({
        ...old,
        selectedRecurAmount: amount
      }))
    },
    [setSession]
  )

  /**
   * Set the selected recurrence unit
   */
  const setSelectedRecurUnit = useCallback(
    (unit: 'Once off' | 'Same days every week') => {
      setSession(old => ({
        ...old,
        selectedRecurUnit: unit
      }))
    },
    [setSession]
  )

  /**
   * Delete a booking for a specific date
   */
  const deleteBookingForDate = useCallback(
    async (date: Date) => {
      try {
        setLoading(true)
        setError(null)

        if (!session.user?.uid) {
          throw new Error('User authentication required')
        }

        await CalendarService.deleteBookingForDate(date, session.user.uid)

        // Refresh bookings after deletion
        await refreshBookingsForDate(new Date(session.selectedFromDate))
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete booking'
        setError(errorMessage)
        console.error('Error deleting booking for date:', err)
      } finally {
        setLoading(false)
      }
    },
    [session.user?.uid, session.selectedFromDate, refreshBookingsForDate]
  )

  /**
   * Load notifications for a date range
   */
  const loadNotificationsForDateRange = useCallback(
    async (startDate: Date, numDays: number) => {
      try {
        setLoading(true)
        setError(null)

        // Calculate end date
        const endDate = new Date(startDate)
        endDate.setDate(startDate.getDate() + numDays - 1)

        const startDateStr = startDate.toISOString().slice(0, 10)
        const endDateStr = endDate.toISOString().slice(0, 10)

        // Fetch all notifications in the date range with a single query
        const allNotifications = await getOfficeNotificationsByDate(
          startDateStr,
          endDateStr
        )

        // Group notifications by date
        const notifByDate: Record<string, Database.OfficeNotification[]> = {}

        // Initialize all dates with empty arrays
        for (let i = 0; i < numDays; i++) {
          const dateObj = new Date(startDate)
          dateObj.setDate(startDate.getDate() + i)
          const dateStr = dateObj.toISOString().slice(0, 10)
          notifByDate[dateStr] = []
        }

        // Group fetched notifications by date
        allNotifications.forEach(notification => {
          if (notification.date && notifByDate[notification.date]) {
            notifByDate[notification.date]?.push(notification)
          }
        })

        setNotifications(notifByDate)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load notifications'
        setError(errorMessage)
        console.error('Error loading notifications for date range:', err)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Load initial notifications when startDate or numOfDays changes
  useEffect(() => {
    loadNotificationsForDateRange(initialStartDate, initialNumOfDays)
  }, [initialStartDate, initialNumOfDays, loadNotificationsForDateRange])

  const actions: CalendarActions = {
    refreshBookingsForDate,
    setSelectedRecurAmount,
    setSelectedRecurUnit,
    deleteBookingForDate,
    loadNotificationsForDateRange
  }

  return [calendarData, actions]
}

export default useCalendarData
